import React, { useState, useEffect } from 'react';
import ShipmentDetailsModal from '../components/shipping/ShipmentDetailsModal';
import CourierPickupModal from '../components/shipping/CourierPickupModal';
import NewShipmentForm from '../components/shipping/NewShipmentForm';
import ShipmentsList from '../components/shipping/ShipmentsList';
import TrackingTab from '../components/shipping/TrackingTab';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useShippingData, useCreateShipment, useTracking, useShipmentFilters } from '../hooks/useShippingData';

const tabStyle = (isActive) => ({
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: isActive ? '600' : '500',
  border: isActive ? '1px solid var(--primary)' : '1px solid var(--border)',
  cursor: 'pointer',
  backgroundColor: isActive ? 'var(--primary)' : 'var(--card-bg)',
  color: isActive ? '#fff' : 'var(--text-secondary)'
});

const ShippingPage = () => {
  const { locationId } = useLocation();
  const { hasPermission } = usePermissions();

  const canAccessShipping = hasPermission('wysylka', 'dostep', locationId);
  const canViewShipments = hasPermission('wysylka', 'podglad', locationId);
  const canCreateShipments = hasPermission('wysylka', 'tworzenie', locationId);
  const canEditShipments = hasPermission('wysylka', 'edycja', locationId);
  const canDownloadLabels = hasPermission('wysylka', 'etykiety', locationId);
  const canOrderPickups = hasPermission('wysylka', 'odbiory', locationId);

  const [activeTab, setActiveTab] = useState('new');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedForPickup, setSelectedForPickup] = useState([]);
  const [showPickupModal, setShowPickupModal] = useState(false);

  const {
    shipments, shipmentsLoading, stats, syncing,
    loadShipments, handleSyncTracking, handleRefreshStatus,
    handleDownloadLabel, handleCancelShipment,
  } = useShippingData();

  const createShipment = useCreateShipment();
  const tracking = useTracking();
  const filters = useShipmentFilters(shipments);

  useEffect(() => {
    if (activeTab === 'list') loadShipments();
  }, [activeTab]);

  // Po odswiezeniu listy uzgodnij wybor do odbioru: usun pozycje ktorych
  // juz nie ma na liscie lub ktore zostaly anulowane/zwrocone, aby nie zamowic
  // kuriera po nieaktualnej przesylce.
  useEffect(() => {
    setSelectedForPickup(prev => {
      if (prev.length === 0) return prev;
      const next = prev.filter(sel => {
        const current = shipments.find(s => s.id === sel.id);
        return current && !['cancelled', 'returned_to_sender'].includes(current.status);
      });
      return next.length === prev.length ? prev : next;
    });
  }, [shipments]);

  const toggleSelectForPickup = (shipment) => {
    setSelectedForPickup(prev => {
      const isSelected = prev.some(s => s.id === shipment.id);
      return isSelected ? prev.filter(s => s.id !== shipment.id) : [...prev, shipment];
    });
  };

  const toggleSelectAll = () => {
    const eligible = filters.paginatedShipments.filter(s =>
      ['created', 'confirmed', 'offers_prepared'].includes(s.status)
    );
    const allSelected = eligible.every(s => selectedForPickup.some(sel => sel.id === s.id));
    if (allSelected) {
      setSelectedForPickup(prev => prev.filter(s => !eligible.some(e => e.id === s.id)));
    } else {
      setSelectedForPickup(prev => {
        const next = [...prev];
        eligible.forEach(s => { if (!next.some(sel => sel.id === s.id)) next.push(s); });
        return next;
      });
    }
  };

  if (!canAccessShipping && !canViewShipments) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: 'var(--bg)', minHeight: '100vh' }}>
        <div style={{
          backgroundColor: 'var(--warning-light)', color: 'var(--warning)', padding: '1.5rem',
          borderRadius: '8px', border: '1px solid var(--warning)', maxWidth: '500px', margin: '2rem auto'
        }}>
          <h5 style={{ marginBottom: '0.5rem' }}>Brak dostepu</h5>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Nie masz uprawnien do modulu Wysylka. Skontaktuj sie z administratorem.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontSize: '12px' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '0', borderRadius: 'var(--border-radius) var(--border-radius) 0 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Wysylka</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Zarzadzanie przesylkami InPost | {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', backgroundColor: 'var(--bg-secondary)', padding: '0 1rem', borderBottom: '1px solid var(--border-color)' }}>
        {canCreateShipments && (
          <button style={tabStyle(activeTab === 'new')} onClick={() => setActiveTab('new')}>
            Nowa przesylka
          </button>
        )}
        {canViewShipments && (
          <button style={tabStyle(activeTab === 'list')} onClick={() => setActiveTab('list')}>
            Przesylki
          </button>
        )}
        <button style={tabStyle(activeTab === 'tracking')} onClick={() => setActiveTab('tracking')}>
          Sledzenie
        </button>
      </div>

      {/* Content */}
      <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '0 0 8px 8px', padding: '1rem', marginBottom: '1rem' }}>
        {activeTab === 'new' && canCreateShipments && (
          <NewShipmentForm {...createShipment} onCreateShipment={createShipment.handleCreateShipment} />
        )}

        {activeTab === 'list' && canViewShipments && (
          <ShipmentsList
            stats={stats}
            shipmentsLoading={shipmentsLoading}
            paginatedShipments={filters.paginatedShipments}
            filteredShipments={filters.filteredShipments}
            filterStatus={filters.filterStatus} setFilterStatus={filters.setFilterStatus}
            searchTerm={filters.searchTerm} setSearchTerm={filters.setSearchTerm}
            currentPage={filters.currentPage} setCurrentPage={filters.setCurrentPage}
            perPage={filters.perPage} setPerPage={filters.setPerPage}
            totalPages={filters.totalPages} startIndex={filters.startIndex}
            selectedForPickup={selectedForPickup}
            onToggleSelectForPickup={toggleSelectForPickup}
            onToggleSelectAll={toggleSelectAll}
            onShowPickupModal={() => setShowPickupModal(true)}
            onRefresh={loadShipments}
            onSyncTracking={handleSyncTracking}
            syncing={syncing}
            onShowDetails={(s) => { setSelectedShipment(s); setShowDetailsModal(true); }}
            onRefreshStatus={handleRefreshStatus}
            onDownloadLabel={handleDownloadLabel}
            onCancelShipment={handleCancelShipment}
            canOrderPickups={canOrderPickups}
            canDownloadLabels={canDownloadLabels}
            canEditShipments={canEditShipments}
          />
        )}

        {activeTab === 'tracking' && (
          <TrackingTab
            trackingNumber={tracking.trackingNumber}
            setTrackingNumber={tracking.setTrackingNumber}
            trackingResult={tracking.trackingResult}
            tracking={tracking.tracking}
            trackingError={tracking.trackingError}
            onTrack={tracking.handleTrack}
          />
        )}
      </div>

      <ShipmentDetailsModal
        shipment={selectedShipment}
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setSelectedShipment(null); }}
        onRefresh={loadShipments}
      />

      <CourierPickupModal
        isOpen={showPickupModal}
        onClose={() => setShowPickupModal(false)}
        selectedShipments={selectedForPickup}
        onSuccess={() => { setSelectedForPickup([]); loadShipments(); }}
      />
    </div>
  );
};

export default ShippingPage;
